import * as React from 'react';
import { Action as ReduxAction } from 'redux';

import { Messages } from '../../../../codegen/Localize';
import { CommonStyles } from '../../../../common/react/CommonStyles';
import { imgPath } from '../../../../common/utilities/ImgUrl';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { SpaceInfoItemTypeValue } from '../../../../ts/models/ContentType';
import { SiteRolesResource } from '../../../../ts/resources/SiteRoles';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { IProjectActions } from '../../../../ts/ServerApi/types/IProjectActions';
import { createNewFlow, createNewProject, createNewSchedule, createNewSite, createNewWorkbook } from '../../../actions/spaces/ContentExplorerCreateActions';
import { selectAvailableActionsForEntity } from '../../../selectors/EntityActionsSelectors';
import { SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '../../../selectors/SessionInfoSelectors';
import { AppState } from '../../../state/AppState';
import { SpaceType } from '../../../state/SpaceInfo';

export interface ICreateContentAction {
  contentType: ExploreContentApi_ContentType;
  isVisible(state: AppState, spaceType: SpaceType): boolean;
  isClickable(state: AppState, projectId?: string): boolean;
  createReduxAction(projectId?: string): ReduxAction;
  icon: string;
  title: (spaceType: SpaceType) => string | JSX.Element;
}

export const CreateFlowAction: ICreateContentAction = {
  contentType: 'flow',
  title: (spaceType: SpaceType) => {
    if (spaceType === SpaceInfoItemTypeValue.datasource) {
      return <div>{Messages.flow()}<span style={{color: CommonStyles.Colors.GreyTextColor}}>{Messages.createContentUsingThisDataSourceMessage()}</span></div>;
    }

    return Messages.flow();
  },
  icon: imgPath('spaces_small_flow.svg'),
  isVisible: () => FeatureFlags.MaestroFlowEditingServerV1,
  isClickable: () => true, // TFSID 889168 - Determine roles and permissions for create flow buttons visible/clickable
  createReduxAction: (): ReduxAction => createNewFlow(),
};

export const CreateProjectAction: ICreateContentAction = {
  contentType: 'project',
  title: Messages.project,
  icon: imgPath('spaces_small_projects.svg'),
  isVisible: (_appState: AppState, spaceType: SpaceType) => {
    return spaceType !== SpaceInfoItemTypeValue.datasource;
  },
  isClickable: (appState: AppState, projectId?: string) => {

    // If the user is site or server admin they can create a project
    // We need to check for server/site admin  to check permission for top level site
    if (SessionUserSelectors.isSessionServerAdmin(appState)) {
      return true;
    }

    if (SiteRolesResource.isSiteAdmin(SessionSiteSelectors.siteRole(appState))) {
      return true;
    }

    if (projectId) {
      const availableActions = selectAvailableActionsForEntity(appState, {contentType: 'project', id: projectId}) as IProjectActions;
      if (availableActions && availableActions.createProject){
        return true;
      }
    }

    return false;
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewProject(projectId);
  }
};

export const CreateWorkbookAction: ICreateContentAction = {
  contentType: 'workbook',
  title: (spaceType: SpaceType) => {
    if (spaceType === SpaceInfoItemTypeValue.datasource) {
      return <div>{Messages.workbook()}<span style={{color: CommonStyles.Colors.GreyTextColor}}>{Messages.createContentUsingThisDataSourceMessage()}</span></div>;
    } else {
      return Messages.workbook();
    }
  },
  icon: imgPath('spaces_small_workbooks.svg'),
  isVisible: (appState: AppState) => {
    return FeatureFlags.webCreateNewWorkbookEnabled;
  },
  isClickable: (appState: AppState, projectId?: string) => {
    return SessionUserSelectors.canAuthorOnWeb(appState);
  },
  createReduxAction: (projectId?: string): ReduxAction => {
    return createNewWorkbook(projectId);
  }
};

export const CreateSiteAction = {
  title: () => Messages.newSite(),
  icon: imgPath('site_content_type_small.svg'),
  isVisible: (_appState: AppState, spaceType: SpaceType) => spaceType === 'sites',
  isClickable: (appState: AppState) => {
    return !SessionServerSelectors.siteAdminSettingsEnabled(appState);
  },
  createReduxAction: (): ReduxAction => {
    return createNewSite();
  }
};

export const CreateScheduleAction = {
  title: () => Messages.newSchedule(),
  icon: imgPath('schedule_content_type_small.svg'),
  isVisible: (appState: AppState, spaceType: SpaceType) => spaceType === 'schedules' && SessionUserSelectors.isSessionServerAdmin(appState),
  isClickable: () => true,
  createReduxAction: (): ReduxAction => {
    return createNewSchedule();
  }
};

export const OrderedCreateContentActions = [
  CreateProjectAction,
  CreateWorkbookAction,
  CreateFlowAction,
];
